import type { ComponentContentSchema } from '@vibesites/runtime';
import { usePropertyField, useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { ctaButtonCss } from '../utils/ctaHover';
import { CtaArrow } from './shared/CtaArrow';

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function ApplyNowAccess() {
  const applyUrl = usePropertyField('property_apply_now_url') as string || '';
  // Phone is a content token that defaults to the call tracking phone (see contentSchema below).
  // Falls back to property_phone if the property has no tracking number configured.
  const phoneToken = useContentToken('applynowaccess.phone') as string | null;
  const fallbackPhone = usePropertyField('property_phone') as string || '';
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;
  const applyCta = useContentToken('applynowaccess.apply_cta');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="ana-section">
      <div ref={sectionRef} className="reveal-fade-up ana-container">
        <RichTextContent
          path="applynowaccess.title"
          as="h2"
          className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text)] mb-8 text-center"
        />
        <div className="ana-grid">
          {/* Online Application Card */}
          <div className="ana-card">
            <div className="ana-card-icon-wrap ana-card-icon--apply">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11zm-9-4h8v2H9v-2zm0-3h8v2H9v-2zm0-3h5v2H9V10z" fill="currentColor"/>
              </svg>
            </div>
            <RichTextContent
              path="applynowaccess.apply_title"
              as="h3"
              className="ana-card-title"
            />
            <RichTextContent
              path="applynowaccess.apply_desc"
              as="p"
              className="ana-card-desc"
            />
            {applyUrl ? (
              <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="ana-btn ana-btn--primary">
                <EditableToken
                  path="applynowaccess.apply_cta"
                >
                  {applyCta || 'Start Application'}
                </EditableToken>
                <CtaArrow />
              </a>
            ) : (
              <span className="ana-btn ana-btn--disabled">Application URL Not Available</span>
            )}
          </div>

          {/* Need Help Card */}
          <div className="ana-card">
            <div className="ana-card-icon-wrap ana-card-icon--help">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" fill="currentColor"/>
              </svg>
            </div>
            <RichTextContent
              path="applynowaccess.help_title"
              as="h3"
              className="ana-card-title"
            />
            <RichTextContent
              path="applynowaccess.help_desc"
              as="p"
              className="ana-card-desc"
            />
            {phone ? (
              // Render a real anchor so the phone is an actual tel: link in the browser.
              // EditableToken lives inside the anchor (as a span) so the displayed number
              // stays selectable/editable in edit mode while the wrapping <a> supplies
              // the href. The EditableToken click handler calls preventDefault, so
              // clicking the number in edit mode won't trigger a phone call.
              <a
                href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                type="call"
                className="dniphone dniphonehref ana-btn ana-btn--outline"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4.64 1.33A.67.67 0 015.33 1H7a.67.67 0 01.64.47l.53 1.6a.67.67 0 01-.17.69L6.88 4.88a8 8 0 003.24 3.24l1.12-1.12a.67.67 0 01.69-.17l1.6.53A.67.67 0 0114 8v1.67a.67.67 0 01-.67.66A10 10 0 013.33 1.67.67.67 0 014 1h.64z" fill="currentColor"/></svg>
                <EditableToken
                  path="applynowaccess.phone"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            ) : (
              <span className="ana-btn ana-btn--disabled">Phone Not Available</span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .ana-section {
          padding: 2rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ana-section { padding: 2.5rem 0 4rem; }
        }
        .ana-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ana-container { padding: 0 1.5rem; } }

        .ana-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .ana-grid { grid-template-columns: 1fr 1fr; }
        }

        .ana-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2.5rem 2rem;
          background: #f8f9fa;
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .ana-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .ana-card-icon-wrap {
          width: 56px; height: 56px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 14px;
          margin-bottom: 1.25rem;
        }
        .ana-card-icon--apply {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }
        .ana-card-icon--help {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }

        .ana-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
        }
        .ana-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 0 0 1.5rem;
          max-width: 22rem;
        }

        .ana-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          padding: 0.75rem 1.75rem;
          border-radius: 9999px;
          text-decoration: none;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.1s, box-shadow 0.2s;
          margin-top: auto;
        }
        ${ctaButtonCss(['.ana-btn--primary', '.ana-btn--outline'])}

        .ana-btn--disabled {
          background: var(--vibe-color-border, #e5e7eb);
          color: var(--vibe-color-text-muted);
          border: none;
          cursor: default;
          opacity: 0.7;
        }
        .ana-btn--disabled:hover { opacity: 0.7; transform: none; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyNowAccess',
  displayName: 'Apply Now Access',
  tokens: {
    'applynowaccess.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue: 'Ready to Apply?',
    },
    'applynowaccess.apply_title': {
      label: 'Application Card Title',
      type: 'richtext',
      defaultValue: 'Online Application',
    },
    'applynowaccess.apply_desc': {
      label: 'Application Card Description',
      type: 'richtext',
      defaultValue: '<p>Complete your application online in just a few minutes. Submit your information securely and take the next step toward your new home.</p>',
    },
    'applynowaccess.apply_cta': {
      label: 'Application Button Text',
      type: 'text',
      defaultValue: 'Start Application',
    },
    'applynowaccess.help_title': {
      label: 'Help Card Title',
      type: 'richtext',
      defaultValue: 'Questions? Call Us',
    },
    'applynowaccess.help_desc': {
      label: 'Help Card Description',
      type: 'richtext',
      defaultValue: '<p>Our leasing team is happy to walk you through the application process, answer questions about requirements, or help you get started.</p>',
    },
    'applynowaccess.phone': {
      label: 'Phone Number',
      type: 'phone',
      // Default to the call-tracking number so analytics can attribute
      // apply-page calls separately from other leasing calls.
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      // Empty so a missing call-tracking number falls through to property_phone
      // in the component (placeholder is applied there as the final fallback).
      defaultValue: '',
    },
  },
};

(ApplyNowAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ApplyNowAccess;
